import _ from 'underscore';
import jQuery from 'jquery';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import raw_urls from './urls';
import events from './events';

// Session object we use for the source of the token, expires_at, etc.
// If it is null, some functions here will complain
var session; 

// retry_fetch.set({});
// Default values to pass to a jquery xhr request
var xhr_defaults = {
    crossDomain : { crossDomain: true },
    xhrFields : { withCredentials: true }
};
// Url template functions. Handlebars functions.
var urls = {};

// name of the target header used for v3 requests
// requiring v5 permissions
var target_header = 'X-Target-Account';

// The event channel you can listen for events on the API, such as
// 'success', 'error', 'complete', '401', etc.
var api_channel = Radio.channel('api');
function setChannel(channel) { events.channel = channel; }
function getChannel() { return api_channel; }
setChannel(api_channel);

// origin is going to end up being 'https://api.electricimp.com:80'
// or something like that. It gets prepended to all the api urls.
// Don't set this directly; use api.setOrigin(origin), which will
// also rebuild all the url paths.
var origin = '';
function getOrigin() { return origin; }
function setOrigin(new_origin) {
    origin = new_origin;
    build_url_funcs();
}

function getClientHost() {
    var host;
    try {
        host = window.location.host;
    } catch (e) {
        console.error(e);
        host = 'unsupported';
    }
    return host;

}

// Token is the apiv4 token used in headers in every v4 call
var token_expiry_timer = null;

function set_session(new_session) {
    session = new_session;
}

function start_expiry_timer(expiry) {
    clearTimeout(token_expiry_timer);
    token_expiry_timer = setTimeout(function() {
        api_channel.trigger('invalidSession');
    }, expiry.getTime() - Date.now());
}

function stop_expiry_timer() {
    clearTimeout(token_expiry_timer);
}

function make_url_func(path, key) {
    var origin_path = [origin, path].join('');
    urls[key] = Handlebars.compile(origin_path);
}

function build_url_funcs() {
    _(raw_urls).each(make_url_func);
}
/**
 * Parsing functions to include with v5 models and collections.
 * These assume the model or collection will provide a relationships
 * handler function called 'parse_relationships'. This is the place for
 * each model definition to handle what exactly is to be done with the
 * relationships passed in the response. Before relationships are parsed,
 * these parse_* functions call a radio command to store some data. It is
 * up to the app (i.e. not this API lib) to provide a handler for the
 * data and set up a request handler for it.
 */

/**
 * If the reponse has some included stuff, trigger an event to save
 * the data for someone else to request it.
 */
function parse_included(resp) {
    if (resp.included) {
        resp.included.forEach(function(item) {
            api_channel.request(item.type, item);
        });
    }
}
/**
 * Parse function for a v5 model. eg.
 *   MyModel = Backbone.Model.extend({ parse: Api.parse_item });
 */
function parse_item(resp) {
    // 204 response handling, shim in a response if non is sent
    if (resp === undefined) {
        resp = {};
        resp.data = [];
    }
    var data = resp.data ? resp.data : resp;
    // var item = _.extend({ id: data.id }, data.attributes);
    var item = data;
    // Before, we ignored included if the response looked like this:
    // resp: { data: { }, included: [], }
    if (!data.included) {
        data.included = resp.included;
    }
    parse_included(data);
    // The model should provide its own parse_relationships function if
    // you want it to do something with the relationships found in the
    // request. eg. associate it with some data found in api data that
    // we passed in the 'data' command above.
    if (resp.relationships && _.isFunction(this.parse_relationships)) {
        this.parse_relationships(resp.relationships);
    }
    return item;
}
function parse_collection(resp) {
    var data = resp.data ? resp.data : resp;
    parse_included(resp);
    if (_.isArray(data)) {
        return data.map(parse_item.bind(this));
    } else {
        return parse_item(data);
    }
}
/**
 * Return some default headers for use in backbone.sync or $.ajax calls
 */
function getHeaderDefaults(options) {
    var token = (session && session.get && session.get('access_token'))
                || session.access_token;
    if (!token) console.warn('Couldn\'t find an access token to use in headers');
    var headers = {};
    if (token && !(options && options.noauth)) {
        headers['Authorization'] = "Bearer " + token;
    }
    // x-ei-ide header tells the api that the request is coming from the ide
    var apiHost = getClientHost();
    if (apiHost) headers['X-EI-Ide'] = apiHost;
    return headers;
}
// Sync method to use instead of Backbone.sync. Encasulates the default
// xhr values and headers we'll need for most requests.
function sync(method, model, options) {
    options = options || {};
    applyDefaultAjaxOptions(options);
    if (method !== 'read') { model.ignore_remote_updates = true; }
    return Backbone.sync(method, model, options);
}
/**
 * Try to recover from a 401 error
 * @param dfd the Deferred object we will either resolve or reject
 * @param func the function to retry, could be Backbone.sync or $.ajax
 * @param args the arguments for the retry (an array)
 */
function try_recover_from_401(dfd, func, args, xhr){
    if (xhr.status != 401) {
        return dfd.reject(xhr);
    }
    if ( args[0] && args[0].url && args[0].url.indexOf('password') > -1 ) {
        return dfd.reject(xhr);
    }
    if (!session) {
        console.error('Api.session was null; can\'t refresh token');
        return dfd.reject.apply(this, arguments);
    }
    function retry_func() {
        return func.apply(null, args);
    }
    function trigger_invalid_token() {
        api_channel.trigger('invalidSession');
        return dfd.reject.apply(this, arguments);
    }

    return session.refresh_token()
        .then(dfd.resolve, trigger_invalid_token)
        .done(retry_func)
        .fail(dfd.reject);
}
/**
 * Return a jquery deferred object like jqxhr, but prevent
 * certain types of failures from passing through, such as
 * 401, before trying to refresh the token and trying the 
 * request again
 */
function deferred_sync(method, model, options) {
    options = options || {};
    // We have to save the original options because Backbone.sync
    // overwrites and adds many more keys to the options. We want
    // to preserve what the original requester requested
    // in the event that we need to retry the request.
    var retry_args = [method, model, _.clone(options)];
    // This deferred object is what will be passed back.
    var dfd = jQuery.Deferred();
    var xhr = sync.call(this, method, model, options);
    xhr.fail(try_recover_from_401.bind(null, dfd, sync, retry_args))
        .done(dfd.resolve);
    return dfd;
}
function deferred_ajax_v5(options) {
    options = options || {};
    // We have to save the original options because Backbone.sync
    // overwrites and adds many more keys to the options. We want
    // to preserve what the original requester requested
    // in the event that we need to retry the request.
    var retry_args = [_.clone(options)];
    // This deferred object is what will be passed back.
    var dfd = jQuery.Deferred();
    // Call this function in the event of the initial request failing.
    // We'll try to handle a 401 case by retrying the original request.
    var xhr = ajax_v5.call(this, options);
    xhr.fail(try_recover_from_401.bind(null, dfd, ajax_v5, retry_args))
        .done(dfd.resolve);
    return dfd;
}
/**
 * Modify ajax options to include a header.
 * This enables v3 and v4 endpoints to be queried on behalf of an
 * account who has granted v5 permissions to another account.
 */
function proxy_options(options, account_id) {
    options = (options || {});
    options.headers = (options.headers || {});
    if (!options.headers[target_header]) {
        options.headers[target_header] = account_id;
    }
    return options;
}
// Generate a sync function with some extra options to pass along
function generate_sync(options) {
    options = (options || {});
    if (options.proxy) {
        // proxy is expected to be an Account with an id or
        // an object with an element { id: id } or the id itself
        var account_id = options.proxy.id || options.proxy;
        return function proxy_sync(method, model, options) {
            options = proxy_options(options, account_id);
            return sync(method, model, options);
        };
    } else {
        return sync;
    }
}
/**
 * Sync-like function which builds success and error handlers
 * like 'fetch' and 'save' but lets us do custom data objects,
 * since these endpoints are kinda messed up as far as RESTfulness
 * goes.
 * TODO: This is here because we need to use it both in v3 Model
 * and the Code Model, but some of might be accomplished by passing
 * particular options to a regular sync call (above).
 */
var syncCustomData = function(method, model, options) {
    options = options ? _.clone(options) : {};
    options.data = options.data || model.toJSON(options);
    options.contentType = 'application/json';

    // Error handler
    var error = options.error;
    options.error = function(resp) {
        if (error) error(model, resp, options);
        model.trigger('error', model, resp, options);
    };

    // Success handler
    var success = options.success;
    options.success = function(resp) {
        if (success) success(model, resp, options);
        model.trigger('sync', model, resp, options);
    };
    return model.sync(method, model, options);
};
/**
 * Apply the default set of ajax options for either sync() or ajax()
 * to an object passed to it. Changes the object.
 */
function applyDefaultAjaxOptions(options) {
    var headerDefaults = getHeaderDefaults(options);
    if (!options.headers) options.headers = {};
    _.defaults(options.headers, headerDefaults);

    // Set XHR cross-domain stuff (CORS)
    if (options.crossDomain) _.defaults(options, xhr_defaults.crossDomain);
    else options.crossDomain = xhr_defaults.crossDomain;

    if (options.xhrFields) _.defaults(options, xhr_defaults.xhrFields);
    else options.xhrFields = xhr_defaults.xhrFields;

    // create a function in options with the same name as one found in obj,
    // and call the function by the same name passed in via options.
    // Basically the same as how Backbone's success/error wrappers work.
    if (!options.statusCode) options.statusCode = {};
    function wrap_function(obj, f) {
        // If the function name f is numeric, we should stuff this
        // function in the statusCode object
        var dest = parseInt(f, 10) ? options.statusCode : options;
        var func = options[f];
        dest[f] = function() {
            if (func) func.apply(options.context, arguments);
            obj[f].apply(obj, arguments);
        };
    }
    // Set up ajax function arrays. We're going to add event handlers
    // but we don't want to overwrite any other success, error, complete
    // handlers already sent to the ajax request.
    [events.events, events.statusCode].forEach(function(obj) {
        Object.keys(obj).forEach(_.partial(wrap_function, obj));
    });
}
/**
 * A custom ajax function which includes all of the necessary XHR
 * headers and authentication for a one-off ajax call
 **/
function ajax() {
    var settings = arguments[0];
    if (_.isObject(settings)) {
        applyDefaultAjaxOptions(settings);
    }
    return Backbone.ajax(settings);
}
function ajax_v5(options) {
    options.headers =  _.extend({
        'Content-type': 'application/vnd.api+json'
    }, options.headers);

    return ajax(options);
}
// The api object we'll use
var api = {
    urls: urls,
    xhr_defaults: xhr_defaults,

    parse_item: parse_item,
    parse_collection: parse_collection,

    proxy_options: proxy_options,

    sync: deferred_sync,
    target_header: target_header,
    syncCustomData: syncCustomData,
    generate_sync: generate_sync,
    ajax: ajax,
    ajax_v5: deferred_ajax_v5,

    getChannel: getChannel,

    getOrigin: getOrigin,
    setOrigin: setOrigin,
    set_session: set_session,
    start_expiry_timer: start_expiry_timer,
    stop_expiry_timer: stop_expiry_timer,
    getHeaderDefaults: getHeaderDefaults,
    build_url_funcs: build_url_funcs
};

export default api;

